/**
 * meeting-portal-service API 客户端（与 Electron portal/api.ts 字段对齐）。
 */
import { getPortalApiBaseUrl } from './config';
import logger from './logger';
export class PortalApiError extends Error {
    code;
    maxConcurrentDevices;
    openDeviceCount;
    status;
    constructor(status, message, code, extras) {
        super(message);
        this.status = status;
        this.code = code;
        this.openDeviceCount = extras?.openDeviceCount;
        this.maxConcurrentDevices = extras?.maxConcurrentDevices;
        this.name = 'PortalApiError';
    }
}
async function request(path, options = {}, accessToken) {
    const headers = {
        'Content-Type': 'application/json',
        ...(options.headers || {})
    };
    if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
    }
    const url = `${getPortalApiBaseUrl()}${path}`;
    logger.info(`请求门户 API | method=${options.method || 'GET'} path=${path}`);
    let response;
    try {
        response = await fetch(url, {
            ...options,
            headers
        });
    }
    catch (err) {
        logger.error(`门户 API 网络失败 | path=${path} error=${err?.message}`);
        throw new PortalApiError(0, err?.message || '网络错误');
    }
    if (response.status === 204) {
        return undefined;
    }
    const contentType = response.headers.get('content-type') || '';
    const text = await response.text();
    const looksLikeHtml = Boolean(contentType.includes('text/html')
        || /^\s*<(!DOCTYPE|html)/i.test(text || ''));
    // 8080 开发服若把 /api 代理到未反代门户的机器，会 200 返回 Meet HTML → 列表 .map 白屏
    if (looksLikeHtml) {
        logger.error(`门户 API 返回 HTML | path=${path} url=${url} contentType=${contentType}`);
        throw new PortalApiError(response.status || 502, `门户 API 返回了网页而非 JSON。请确认 ${getPortalApiBaseUrl()} 已正确反代 /api`
            + '（本地 8080 需 WEBPACK_DEV_SERVER_PROXY_TARGET 指向带门户的 8443）', 'INVALID_RESPONSE');
    }
    let body = null;
    if (text) {
        try {
            body = JSON.parse(text);
        }
        catch {
            logger.error(`门户 API JSON 解析失败 | path=${path}`);
            throw new PortalApiError(response.status || 502, `门户 API 响应不是合法 JSON（${path}）`, 'INVALID_RESPONSE');
        }
    }
    if (!response.ok) {
        logger.warn(`门户 API 失败 | path=${path} status=${response.status} code=${body?.code}`);
        throw new PortalApiError(response.status, body?.message || `HTTP ${response.status}`, body?.code, {
            maxConcurrentDevices: body?.maxConcurrentDevices,
            openDeviceCount: body?.openDeviceCount
        });
    }
    return body;
}
/**
 * 上传头像/背景：不要手动设 Content-Type，由浏览器带 multipart boundary。
 */
async function uploadUserMedia(path, file, accessToken) {
    const form = new FormData();
    form.append('file', file, file instanceof File ? file.name : 'image.jpg');
    const url = `${getPortalApiBaseUrl()}${path}`;
    logger.info(`请求门户 API | method=POST path=${path} multipart=true`);
    let response;
    try {
        response = await fetch(url, {
            body: form,
            headers: {
                Authorization: `Bearer ${accessToken}`
            },
            method: 'POST'
        });
    }
    catch (err) {
        logger.error(`门户 API 网络失败 | path=${path} error=${err?.message}`);
        throw new PortalApiError(0, err?.message || '网络错误');
    }
    const text = await response.text();
    let body = null;
    if (text) {
        try {
            body = JSON.parse(text);
        }
        catch {
            logger.error(`门户 API JSON 解析失败 | path=${path}`);
            throw new PortalApiError(response.status || 502, `门户 API 响应不是合法 JSON（${path}）`, 'INVALID_RESPONSE');
        }
    }
    if (!response.ok) {
        logger.warn(`门户 API 失败 | path=${path} status=${response.status} code=${body?.code}`);
        throw new PortalApiError(response.status, body?.message || `HTTP ${response.status}`, body?.code, {
            maxConcurrentDevices: body?.maxConcurrentDevices,
            openDeviceCount: body?.openDeviceCount
        });
    }
    return body;
}
function deviceJoinQuery(flags) {
    if (!flags || (!flags.addDevice && !flags.reissueToken)) {
        return '';
    }
    const params = new URLSearchParams();
    if (flags.addDevice) {
        params.set('addDevice', 'true');
    }
    if (flags.reissueToken) {
        params.set('reissueToken', 'true');
    }
    return `?${params.toString()}`;
}
export const portalApi = {
    sendSms(phone, purpose) {
        return request('/api/auth/sms/send', {
            method: 'POST',
            body: JSON.stringify({ phone, purpose })
        });
    },
    register(payload) {
        return request('/api/auth/register', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    loginPassword(phone, password) {
        return request('/api/auth/login/password', {
            method: 'POST',
            body: JSON.stringify({ phone, password })
        });
    },
    loginSms(phone, code) {
        return request('/api/auth/login/sms', {
            method: 'POST',
            body: JSON.stringify({ phone, code })
        });
    },
    ssoStatus() {
        return request('/api/auth/sso/status', {
            method: 'GET'
        });
    },
    loginBanbanOauth(code, accessToken) {
        return request('/api/auth/sso/oauth-login', {
            method: 'POST',
            body: JSON.stringify({ code, accessToken })
        });
    },
    me(accessToken) {
        return request('/api/me', { method: 'GET' }, accessToken);
    },
    updateProfile(accessToken, payload) {
        return request('/api/me', {
            method: 'PUT',
            body: JSON.stringify(payload)
        }, accessToken);
    },
    uploadAvatar(accessToken, file) {
        return uploadUserMedia('/api/me/avatar', file, accessToken);
    },
    uploadBackground(accessToken, file) {
        return uploadUserMedia('/api/me/background', file, accessToken);
    },
    /**
     * 向当前绑定手机号发送改密验证码（服务端用登录用户手机号，不接受客户端指定）。
     */
    sendChangePasswordSms(accessToken) {
        return request('/api/me/password/sms', {
            method: 'POST'
        }, accessToken);
    },
    changePassword(accessToken, payload) {
        return request('/api/me/password', {
            method: 'PUT',
            body: JSON.stringify(payload)
        }, accessToken);
    },
    requestAccountDeletion(accessToken) {
        return request('/api/me/deletion', { method: 'POST' }, accessToken);
    },
    getPreferences(accessToken) {
        return request('/api/me/preferences', {
            method: 'GET'
        }, accessToken);
    },
    updatePreferences(accessToken, multiDeviceMode) {
        return request('/api/me/preferences', {
            method: 'PUT',
            body: JSON.stringify({ multiDeviceMode })
        }, accessToken);
    },
    quick(accessToken, options) {
        return request('/api/meetings/quick', {
            method: 'POST',
            body: JSON.stringify({
                title: options?.title,
                meetingNo: options?.meetingNo
            })
        }, accessToken);
    },
    join(accessToken, meetingNo, flags) {
        return request('/api/meetings/join', {
            method: 'POST',
            body: JSON.stringify({
                addDevice: Boolean(flags?.addDevice),
                meetingNo,
                reissueToken: Boolean(flags?.reissueToken)
            })
        }, accessToken);
    },
    /**
     * 会议直链邀请页预览：不写入会记录。
     *
     * @param {string} accessToken 访问令牌.
     * @param {string} meetingNo 会议号.
     * @returns {Promise<IMeetingPreview>}
     */
    preview(accessToken, meetingNo) {
        const query = `?meetingNo=${encodeURIComponent(meetingNo)}`;
        return request(`/api/meetings/preview${query}`, {
            method: 'GET'
        }, accessToken);
    },
    schedule(accessToken, payload) {
        return request('/api/meetings/schedule', {
            method: 'POST',
            body: JSON.stringify(payload)
        }, accessToken);
    },
    enter(accessToken, meetingId, flags) {
        return request(`/api/meetings/${meetingId}/enter${deviceJoinQuery(flags)}`, {
            method: 'POST'
        }, accessToken);
    },
    shareScreen(accessToken) {
        return request('/api/meetings/share-screen', {
            method: 'POST'
        }, accessToken);
    },
    upcoming(accessToken) {
        return request('/api/meetings/upcoming', {
            method: 'GET'
        }, accessToken);
    },
    /**
     * 侧栏「全部会议」：组织者所有 + 参会历史合并，按 sortTime 倒序分页。
     *
     * @param accessToken 访问令牌
     * @param page 页码（0 起）
     * @param size 单页条数（默认 50，上限 200）
     */
    allMeetings(accessToken, page = 0, size = 50) {
        const query = `?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`;
        return request(`/api/meetings/all${query}`, {
            method: 'GET'
        }, accessToken);
    },
    history(accessToken) {
        return request('/api/meetings/history', {
            method: 'GET'
        }, accessToken);
    },
    /**
     * 从历史重新入会：未结束直接进会；已结束仅组织者可重开后再进。
     */
    rejoin(accessToken, meetingId, flags) {
        return request(`/api/meetings/${meetingId}/rejoin${deviceJoinQuery(flags)}`, {
            method: 'POST'
        }, accessToken);
    },
    leave(accessToken, meetingId) {
        return request(`/api/meetings/${meetingId}/leave`, {
            method: 'POST'
        }, accessToken);
    },
    /**
     * 上报会中真实人数（与侧栏「会议参会者 (N人)」同源），供门户空房结束判定。
     */
    reportOccupancy(accessToken, meetingId, participantCount) {
        return request(`/api/meetings/${meetingId}/occupancy`, {
            method: 'POST',
            body: JSON.stringify({ participantCount })
        }, accessToken);
    },
    /**
     * 主持人结束会议（对应 EndConferenceButton → conference.end）。
     * 门户 DB meetings.status 立即写为 ENDED。
     */
    endMeeting(accessToken, meetingId) {
        return request(`/api/meetings/${meetingId}/end`, {
            method: 'POST'
        }, accessToken);
    },
    deleteMeeting(accessToken, meetingId) {
        return request(`/api/meetings/${meetingId}`, {
            method: 'DELETE'
        }, accessToken);
    },
    listNotifications(accessToken) {
        return request('/api/notifications', {
            method: 'GET'
        }, accessToken);
    },
    unreadCount(accessToken) {
        return request('/api/notifications/unread-count', {
            method: 'GET'
        }, accessToken);
    },
    markRead(accessToken, notificationId) {
        return request(`/api/notifications/${notificationId}/read`, {
            method: 'POST'
        }, accessToken);
    },
    markAllRead(accessToken) {
        return request('/api/notifications/read-all', {
            method: 'POST'
        }, accessToken);
    },
    listRecordings(accessToken) {
        return request('/api/recordings', {
            method: 'GET'
        }, accessToken);
    },
    /**
     * 拼接带 access_token 的流式播放地址（供 video 标签使用）。
     * 始终用当前门户基址拼 URL，不信任服务端 streamUrl 的 host（nginx $host 曾丢掉 :8443）。
     */
    getRecordingStreamUrl(accessToken, recordingId, _streamUrl) {
        const clientBase = getPortalApiBaseUrl().replace(/\/$/, '');
        return `${clientBase}/api/recordings/${recordingId}/stream?access_token=${encodeURIComponent(accessToken)}`;
    },
    /**
     * 封面图地址（img 无法带 Authorization，走 query token）。
     */
    getRecordingThumbnailUrl(accessToken, recordingId) {
        const clientBase = getPortalApiBaseUrl().replace(/\/$/, '');
        return `${clientBase}/api/recordings/${recordingId}/thumbnail?access_token=${encodeURIComponent(accessToken)}`;
    },
    deleteRecording(accessToken, recordingId) {
        return request(`/api/recordings/${recordingId}`, {
            method: 'DELETE'
        }, accessToken);
    }
};
